import React from 'react';

export const fetchWithTimeout = async (
    url: string,
    options: RequestInit,
    timeout: number
) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    options.signal = controller.signal;
    try {
        const response = await fetch(url, options);
        clearTimeout(id);
        return response;
    } catch (error) {
        clearTimeout(id);
        return {
            ok: false,
            status: 408,
            json: async () => ({ error: 'request timeout' }),
        }
    }
}


export const getMarketSiteName = (siteId: string, shorten: boolean = false): string => {
    if (siteId == "smartstore") {
        return shorten ? "스토어" : "스마트스토어";
    } else if (siteId == "auction") {
        return "옥션";
    } else if (siteId == "gmarket") {
        return "지마켓";
    } else if (siteId == "eleventh") {
        return "11번가";
    } else if (siteId == "ssgdotcom") {
        return "SSG닷컴";
    } else if (siteId == "lotteon") {
        return "롯데ON";
    }
    return "";
}

export const getCompareSiteName = (siteId: string, shorten: boolean = false): string => {
    if (siteId == "naver") {
        return shorten ? "네이버" : "네이버쇼핑";
    } else if (siteId == "danawa") {
        return "다나와";
    } else if (siteId == "enuri") {
        return "에누리";
    }
    return "";
}

export const formatNumber = (num: number): string => {
    return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
}

export const formatRelativeTime = (dateString: string | null): string => {
  if (!dateString) return "-";
  const now = new Date();
  const date = new Date(dateString);
  const diffInMs = now.getTime() - date.getTime();  
  if (diffInMs < 0) { // 미래 시간인 경우
    return "곧";
  }
  const units = [
    { name: "년", ms: 365 * 24 * 60 * 60 * 1000 },
    { name: "개월", ms: 30 * 24 * 60 * 60 * 1000 },
    { name: "주", ms: 7 * 24 * 60 * 60 * 1000 },
    { name: "일", ms: 24 * 60 * 60 * 1000 },
    { name: "시간", ms: 60 * 60 * 1000 },
    { name: "분", ms: 60 * 1000 },
    { name: "초", ms: 1 * 1000 },
  ];
  for (const unit of units) {
    const value = Math.floor(diffInMs / unit.ms);
    if (value >= 1) {
      return `${value}${unit.name} 전`;
    }
  }  
  return "방금 전";
};


export const getSimpleISO = (isoString: string) => {
    // isoString = "2025-07-30T09:52:41.656475+09:00";
    if (!isoString) {
        return
    }
    const dt = new Date(isoString);
    const year = dt.getFullYear()
    const month = String(dt.getMonth()+1).padStart(2, '0');
    const date = String(dt.getDate()).padStart(2, '0');
    const hours = String(dt.getHours()).padStart(2, '0');
    const minutes = String(dt.getMinutes()).padStart(2, '0');
    const seconds = String(dt.getSeconds()).padStart(2, '0');
    const ymdhms = `${year}-${month}-${date} ${hours}:${minutes}:${seconds}`
    return ymdhms
}

export const getProcessErrorDisplayMessage = (message?: string | null): string | undefined => {
    if (message === "일시적인 작업 환경 오류로 실행할 수 없습니다. 잠시 후 다시 시도해주세요. 문제가 계속되면 관리자에게 문의해주세요.") {
        return "일시적 작업 환경 오류";
    }
    return message ?? undefined;
}

export interface HighlightSearchTermSegment {
    text: string;
    highlighted: boolean;
}

export const highlightSearchTermSegments = (
    text: string,
    searchTerm: string,
): HighlightSearchTermSegment[] => {
    if (!text) {
        return [];
    }
    if (!searchTerm) {
        return [{ text, highlighted: false }];
    }

    const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
    return text
        .split(regex)
        .flatMap((segment, index) => (
            segment ? [{ text: segment, highlighted: index % 2 === 1 }] : []
        ));
}

export const generateTimeOptions = (startTime: string, endTime: string) => {
    const times = ['00:00'];
    for (let hrs = 0; hrs <= 23; hrs++) {
        for (let min = 0; min <= 50; min += 10) {
            if (hrs === 0 && min === 0) continue; // skip duplicate 00:00
            times.push(`${String(hrs).padStart(2, '0')}:${String(min).padStart(2, '0')}`);
        }
    }
    times.push('24:00');

    const startIndex = times.indexOf(startTime);
    const endIndex = times.indexOf(endTime);

    return times.slice(startIndex, endIndex + 1).map(time => {
        const displayTime = time === '24:00' ? '24:00' : time;
        const value = time === '24:00' ? '00:00' : time;
        return React.createElement('option', { key: time, value: value }, displayTime);
    });
};

export type ProcessAdjustFields = {
    message?: string | null;
    is_dry_run?: boolean;
    price_decision_reason?: string | null;
    adjust_strategy?: string | null;
    adjust_amount?: number | null;
    adjust_amount_after_limit?: number | null;
    price?: number | null;
    discount?: number | null;
    price_after_limit?: number | null;
    price_after_adjust?: number | null;
    discount_after_limit?: number | null;
    discount_after_adjust?: number | null;
    discount_clamped_to_zero?: boolean | null;
    price_ll?: number | null;
    price_hl?: number | null;
    discount_ll?: number | null;
    discount_hl?: number | null;
};

export function isDryRunProcessLog(log: { is_dry_run?: boolean; message?: string | null }): boolean {
    return log.is_dry_run === true || Boolean(log.message?.startsWith("드라이런 완료:"));
}

/** WS(adjust_amount_after_limit) / DB(after_limit 필드) 모두에서 실제 적용 조정액을 구한다. */
export function getActualAdjustAmount(log: ProcessAdjustFields): number | undefined {
    if (typeof log.adjust_amount_after_limit === "number") {
        return log.adjust_amount_after_limit;
    }

    if (typeof log.adjust_amount !== "number") {
        return undefined;
    }

    const isPriceAdjustment = !log.adjust_strategy || log.adjust_strategy.startsWith("adjust_price");
    const afterValue = isPriceAdjustment
        ? log.price_after_limit ?? log.price_after_adjust
        : log.discount_after_limit ?? log.discount_after_adjust;

    if (typeof afterValue !== "number") {
        return log.adjust_amount;
    }

    if (isPriceAdjustment) {
        if (typeof log.price !== "number") {
            return log.adjust_amount;
        }
        return afterValue - log.price;
    }

    if (typeof log.discount !== "number") {
        return log.adjust_amount;
    }
    return log.discount - afterValue;
}

export function getProcessAdjustLabels(adjustStrategy?: string | null) {
    const adjustmentLabel = adjustStrategy
        ? adjustStrategy.startsWith("adjust_price") ? "가격" : "할인액"
        : "가격";
    const limitLabel = adjustmentLabel === "가격" ? "가격" : "할인";
    return { adjustmentLabel, limitLabel };
}

export function formatSignedAmount(amount?: number | null): string {
    if (typeof amount !== "number") {
        return "";
    }
    const sign = amount > 0 ? "+" : "";
    return `${sign}${amount.toLocaleString("ko-KR")}`;
}

export function getProcessLimitReason(log: ProcessAdjustFields): string | undefined {
    if (log.discount_clamped_to_zero === true) {
        return "할인 하한";
    }

    const isPriceAdjustment = !log.adjust_strategy || log.adjust_strategy.startsWith("adjust_price");
    const adjustedValue = isPriceAdjustment
        ? log.price_after_adjust
        : log.discount_after_adjust;
    const limitedValue = isPriceAdjustment
        ? log.price_after_limit
        : log.discount_after_limit;

    if (typeof adjustedValue !== "number" || typeof limitedValue !== "number") {
        return undefined;
    }
    if (adjustedValue === limitedValue) {
        return undefined;
    }

    if (isPriceAdjustment) {
        if (limitedValue === log.price_hl) {
            return "가격 상한";
        }
        if (limitedValue === log.price_ll) {
            return "가격 하한";
        }
        return limitedValue < adjustedValue ? "가격 상한" : "가격 하한";
    }
    if (limitedValue === log.discount_hl) {
        return "할인 상한";
    }
    if (limitedValue === log.discount_ll) {
        return "할인 하한";
    }
    return limitedValue < adjustedValue ? "할인 상한" : "할인 하한";
}

export function getProcessResultDescription(log: ProcessAdjustFields): string {
    const dryRunMessage = log.message?.startsWith("드라이런 완료:")
        ? log.message.slice("드라이런 완료:".length).trim()
        : null;
    const isDryRun = isDryRunProcessLog(log);
    if (log.message && !isDryRun) {
        return log.message;
    }

    if (isDryRun && typeof log.adjust_amount !== "number" && log.price_decision_reason !== "keep_default_price") {
        return !dryRunMessage || dryRunMessage === "상점 가격은 변경하지 않았습니다." ? "조정액 미산출" : dryRunMessage;
    }

    const description = getCalculatedProcessResultDescription(log);
    if (!isDryRun) return description;
    return description === "가격 유지" ? "가격 유지 (조정액 0원)" : description;
}

function getCalculatedProcessResultDescription(log: ProcessAdjustFields): string {
    if (log.price_decision_reason === "keep_default_price") {
        const before = log.price;
        const after = log.price_after_limit ?? log.price_after_adjust;
        if (typeof before === "number" && typeof after === "number" && before !== after) {
            return "복구기준가 복원";
        }
        return "복구기준가 유지 (변경 없음)";
    }

    const actualAdjustAmount = getActualAdjustAmount(log);
    const hasAdjustAmount = typeof log.adjust_amount === "number";
    const { adjustmentLabel, limitLabel } = getProcessAdjustLabels(log.adjust_strategy);
    const isLimited = (
        typeof actualAdjustAmount === "number"
        && typeof log.adjust_amount === "number"
        && actualAdjustAmount !== log.adjust_amount
    );
    const formatAmount = (amount?: number | null) => (
        typeof amount === "number" ? amount.toLocaleString("ko-KR") : ""
    );

    if (hasAdjustAmount && (actualAdjustAmount !== 0 || log.adjust_amount !== 0)) {
        if (isLimited && log.discount_clamped_to_zero === true) {
            const attemptedDiscount = (
                typeof log.discount_after_adjust === "number"
                && log.discount_after_adjust < 0
            )
                ? log.discount_after_adjust
                : typeof log.discount === "number"
                    ? log.discount - log.adjust_amount!
                    : log.adjust_amount;
            return `조정 제한 (${formatSignedAmount(attemptedDiscount)}원, 할인 하한)`;
        }
        if (actualAdjustAmount === 0 && isLimited) {
            const limitReason = getProcessLimitReason(log) ?? `${limitLabel} 제한`;
            return `조정 제한 (${formatSignedAmount(log.adjust_amount)}원, ${limitReason})`;
        }
        if (isLimited) {
            return `${adjustmentLabel} 조정: ${formatAmount(actualAdjustAmount)}원 (제한 전: ${formatAmount(log.adjust_amount)}원)`;
        }
        return `${adjustmentLabel} 조정: ${formatAmount(actualAdjustAmount)}원`;
    }

    return "가격 유지";
}

/** 처리완료/포커스 하이라이트 색은 index.css CSS 변수 사용 */
export const ITEM_PROCESS_HIGHLIGHT_DURATION_MS = 5000;
export const ITEM_PROCESS_HIGHLIGHT_TRANSITION = "background-color 0.3s ease";

/** 상품 리스트에서 해당 행으로 스크롤. 행이 없으면 false. */
export function scrollItemRowIntoView(itemId: string | number): boolean {
    const row = document.querySelector(
        `tr[data-item-id="${CSS.escape(String(itemId))}"]`
    ) as HTMLElement | null;
    if (!row) {
        return false;
    }
    row.scrollIntoView({ behavior: "smooth", block: "center" });
    return true;
}

export default {
    getMarketSiteName,
    getCompareSiteName, 
    formatNumber,
    formatRelativeTime,
}
